/**
 * AiSuggestions.tsx — FR-015 / T316.
 *
 * The wired surface for the AI marker-suggestion orphan
 * (specs/exploration/stub_orphan_inventory.md §5 orphan #6):
 *
 *   - api.aiSuggestions.generateSuggestions (public action; requireAuth
 *     in practice — the action delegates to an internal action and reads
 *     the existing marker timestamps)
 *
 * The action is invoked through useMutationWithToast (FR-013 (H1) /
 * FR-015 (O-D)) — the wrapper logs argument KEYS only.
 *
 * Visual composition (lists, helper text) is re-exported from
 * `_bundle.tsx` for layout continuity; this file replaces only the
 * mock-backed "Generate" button with a real binding.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { api } from '@convex/api';
import { Btn, Panel } from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { AiSuggestions as AiSuggestionsShell } from './_bundle';

interface AiSuggestionsProps {
  id?: string;
}

interface SuggestionResult {
  suggestions?: unknown[];
  source?: string;
}

export function AiSuggestions(props: AiSuggestionsProps) {
  // Real action binding — generateSuggestions is exported as a Convex
  // `action`, which is invokable through useMutation in the React SDK.
  const generateM = useMutationWithToast(
    api.aiSuggestions.generateSuggestions,
    {
      logLabel: 'aiSuggestions_generate',
      successMessage: 'Sugerencias generadas.',
      errorMessagePrefix: 'No se pudieron generar las sugerencias',
    },
  );
  const [lastCount, setLastCount] = useState<number | null>(null);

  const handleGenerate = async () => {
    const assetId = props.id;
    if (!assetId) return;
    try {
      const result = (await generateM.run({
        assetId: assetId as any,
        existingMarkerTimestamps: [],
        maxSuggestions: 8,
      })) as SuggestionResult;
      setLastCount(result?.suggestions?.length ?? 0);
    } catch {
      // wrapper already toasted + logged (argKeys only).
    }
  };

  return (
    <div>
      <AiSuggestionsShell {...props} />
      <Panel title="Generar sugerencias (en vivo)" style={{ marginTop: 24 }}>
        <p
          style={{
            margin: '0 0 16px',
            fontSize: 14,
            color: 'var(--body-muted)',
          }}
        >
          Llama a <code>api.aiSuggestions.generateSuggestions</code> contra el
          asset actual. El resultado se aplica al editor de marcadores.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Btn
            variant="solid"
            icon="zap"
            disabled={generateM.loading || !props.id}
            onClick={() => void handleGenerate()}
          >
            Generar sugerencias
          </Btn>
          {lastCount !== null && (
            <span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>
              {lastCount} sugerencia{lastCount === 1 ? '' : 's'} generada
              {lastCount === 1 ? '' : 's'}.
            </span>
          )}
        </div>
      </Panel>
    </div>
  );
}

export default AiSuggestions;
